import getPageTypeLabel from '@dashboard/services/getPageTypeLabel';

const CONTENT_TAB = { checkout: 'product', event: 'event', form: 'form' };
const PEOPLE = { checkout: 'buyers', event: 'attendees', form: 'people' };
const TITLE = { checkout: 'product title', event: 'event name', form: 'form title' };
const CONFIRMATION_SUBJECT = {
  checkout: 'Your order is confirmed',
  event: 'Your booking is confirmed',
  form: 'Thanks, we received your details',
};
const NEW_RECORDS = { checkout: 'payments', event: 'bookings', form: 'submissions' };

const pageTypeLabel = type => getPageTypeLabel({ type });

const ex = (label, prompt) => ({ label, prompt });

const HEARD_ABOUT_US = ex(
  'Add a dropdown asking how they heard about us',
  'Add an optional dropdown asking how they heard about us, with Instagram, Facebook, A friend, Our newsletter and Other.',
);
const DEFAULT_CONFIRMATION = ex(
  'Go back to the default confirmation message',
  'Remove the custom confirmation message and go back to the default heading and text.',
);

const dynamicGuide = (pageType, tab) => ({
  tab,
  path: '/dynamic-info',
  summary: `Choose which details can be changed through URL parameters in your ${pageTypeLabel(pageType)} link.`,
  examples:
    pageType === 'checkout'
      ? [
          ex(
            'Let links set the price',
            'Let links set the price on this page through the URL, so I can share a different price with each group.',
          ),
          ex(
            'Allow links to preselect a price',
            'Let links preselect one of the prices on this page, so a link from my website opens with the right option already chosen.',
          ),
          ex(
            'Stop links from changing this page',
            'Stop links from changing the price or any other details on this page, so everyone sees exactly what I set here.',
          ),
        ]
      : [
          ex(
            'Let links set the title and description',
            `Let links set the title and description of this ${pageType} through the URL, so I can share it with different wording for each group.`,
          ),
          ex(
            'Only let links change the title',
            `Only let links change the title of this ${pageType}, and keep the description and everything else as I set it.`,
          ),
          ex(
            'Stop links from changing this page',
            `Stop links from changing the title, description or anything else on this ${pageType}.`,
          ),
        ],
});

const themeIdOf = theme => (typeof theme === 'string' ? theme : theme?._id);

// A preset first: the store's own themes include ones made for single pages.
function themeToSwitchTo({ page, themes = [] }) {
  const others = themes.filter(theme => theme._id !== themeIdOf(page?.theme) && !theme.deprecated);
  return others.find(theme => theme.scope === 'global') ?? others[0];
}

const themeNamed = name => (/\btheme$/i.test(name) ? name : `${name} theme`);

// The page's own overrides win over its theme, as in the preview.
const pageToken = (page, key) =>
  page?.themeOverrides?.variables?.tokens?.[key] ?? page?.theme?.variables?.tokens?.[key];

const ROUNDED = new Set(['md', 'lg', 'xl', 'full']);

const sameColor = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

// The most common theme requests, each as the change this page doesn't have yet. A theme with no
// main color already uses the store's brand color.
function themeExamples({ page, seller, themes }) {
  const brandColor = seller?.brandColor;
  const primary = pageToken(page, 'colorPrimary');
  const pageColor = (typeof primary === 'string' ? primary : primary?.light) ?? brandColor;
  const target = themeToSwitchTo({ page, themes });

  return [
    brandColor &&
      !sameColor(pageColor, brandColor) &&
      ex(
        `Theme the page using my brand color ${brandColor}`,
        `Theme the page using my brand color ${brandColor} for the buttons, links and highlights, and keep the text easy to read.`,
      ),
    pageToken(page, 'colors') === 'dark'
      ? ex(
          'Switch the page to light mode',
          'Switch the page to light mode, with a light background and dark text, and keep the main color the same.',
        )
      : ex(
          'Switch the page to dark mode',
          'Switch the page to dark mode, with a dark background and light text, and keep the main color the same.',
        ),
    ROUNDED.has(pageToken(page, 'radius') ?? 'sm')
      ? ex(
          'Make the corners square',
          'Make the corners square on the buttons, cards and inputs for a sharper, more formal look.',
        )
      : ex(
          'Round the corners on the buttons and cards',
          'Round the corners on the buttons, cards and inputs for a softer, friendlier look.',
        ),
    target &&
      ex(
        `Switch the page to the ${themeNamed(target.name)}`,
        `Switch the page to the ${themeNamed(target.name)} and keep my wording as it is.`,
      ),
    ex(
      'Change the main color to:',
      'Change the main color of the buttons, links and highlights to:',
    ),
  ]
    .filter(Boolean)
    .slice(0, 3);
}

// One entry per guide key; a function entry is built per page type.
export const DEFINITIONS = {
  details: pageType =>
    pageType === 'event'
      ? {
          tab: 'event',
          path: '/details',
          summary: 'Set your event title, dates, location, and attendance details.',
          examples: [
            ex(
              'Change the start time to 7pm',
              'Change the start time to 7pm on the same day, and move the end time so the event stays the same length.',
            ),
            ex(
              'Make the event end 2 hours after it starts',
              'Make the event end 2 hours after it starts, on the same day, and keep the start time as it is.',
            ),
            ex(
              'Make the event name shorter and clearer',
              'Make the event name shorter and clearer, ideally under 40 characters, so it reads well on tickets and in emails.',
            ),
          ],
        }
      : {
          tab: 'product',
          path: '/details',
          summary: 'Set your product title and pricing options.',
          examples: [
            ex(
              'Make the product title shorter and clearer',
              'Make the product title shorter and clearer, ideally under 40 characters, so it reads well at checkout and in emails.',
            ),
            ex(
              'Let buyers split the price into 3 monthly payments',
              'Add a payment plan so buyers can split the price into 3 monthly payments, and keep the option to pay in full.',
            ),
            ex(
              'Add a yearly price with 2 months free',
              'Add a yearly price next to the monthly one, set to 10 times the monthly price so yearly members get 2 months free.',
            ),
          ],
        },

  description: pageType => ({
    tab: CONTENT_TAB[pageType],
    path: '/description',
    summary: `Add text, images, FAQs, and more to describe your ${CONTENT_TAB[pageType]}.`,
    examples:
      pageType === 'form'
        ? [
            ex(
              'Make the description shorter and friendlier',
              'Make the description shorter and friendlier: two or three short sentences that say what the form is for and how long it takes.',
            ),
            ex(
              'Add a line about what happens after they submit',
              'Add a line to the end of the description about what happens after they submit, such as when we will get back to them.',
            ),
            ex(
              'Add a short note about how we use their details',
              'Add a short note to the description saying we only use their details to reply to this form and never share them.',
            ),
          ]
        : [
            ex(
              'Rewrite the description in a friendlier tone',
              pageType === 'event'
                ? 'Rewrite the description in a warmer, friendlier tone, and keep the date, time, place and what is included.'
                : 'Rewrite the description in a warmer, friendlier tone, and keep every detail about what is included.',
            ),
            ex(
              'Add a FAQ with 3 common questions',
              pageType === 'event'
                ? 'Add a FAQ to the description with 3 common questions about the event, such as parking, what to bring and whether children can come.'
                : 'Add a FAQ to the description with 3 common questions buyers ask, such as what is included, how they get access and the refund policy.',
            ),
            ex(
              'Shorten the description and use bullet points',
              pageType === 'event'
                ? 'Shorten the description to a short intro and a bulleted list of the key details: when, where, who it is for and what is included.'
                : 'Shorten the description to a short intro and a bulleted list of what is included, keeping every key detail.',
            ),
          ],
  }),

  'image-gallery': pageType => ({
    tab: CONTENT_TAB[pageType],
    path: '/image-gallery',
    summary: `Add images to showcase your ${CONTENT_TAB[pageType]}.`,
    coverage: 'partial',
    coverageNote:
      'Adding stock photos, reordering and removing work. Attach your own images with + in the message box.',
    examples: [
      ex(
        'Add a stock photo that matches the description',
        'Read the description and add one stock photo that matches it, as the first image in the gallery.',
      ),
      ex(
        'Move the last image to the front',
        'Move the last image to the front so it becomes the main image, and keep the others in the same order.',
      ),
      ex(
        'Remove the second image',
        'Remove the second image from the gallery and keep the others in the same order.',
      ),
    ],
  }),

  variants: {
    tab: 'product',
    path: '/variants',
    summary: 'Offer options for customers to choose from.',
    examples: [
      ex(
        'Add a Size variant with Small, Medium and Large',
        'Add a Size variant with Small, Medium and Large options, all at the current price.',
      ),
      ex(
        'Let buyers pick more than one option',
        'Let buyers pick more than one option from the variants instead of just one.',
      ),
      ex(
        'Make choosing an option required',
        'Make choosing an option required, so buyers cannot pay until they pick one.',
      ),
    ],
  },

  'order-bumps': {
    tab: 'order-bumps',
    path: '',
    summary: 'Offer products your customer can accept with one click before paying.',
    coverage: 'partial',
    coverageNote:
      'Adding, changing, reordering and removing order bumps work. An order bump other pages also offer is not changed here: a new one is made for this page instead. Uploading your own image for one is not possible here yet.',
    examples: [
      ex(
        'Add a $5 gift wrap order bump',
        'Add a $5 gift wrap order bump with a one-line description saying we wrap it by hand and add a handwritten note.',
      ),
      ex(
        'Preselect the first order bump',
        'Preselect the first order bump so it is already ticked when buyers reach checkout, and leave the others unticked.',
      ),
      ex(
        'Move the last order bump to the top',
        'Move the last order bump to the top of the list and keep the others in the same order.',
      ),
    ],
  },

  'add-ons': {
    tab: 'add-ons',
    path: '',
    summary:
      'Offer extras such as merchandise or parking that the customer can add to their booking with one click.',
    coverage: 'partial',
    coverageNote:
      'Adding, changing, reordering and removing add-ons work. An add-on other pages also offer is not changed here: a new one is made for this event instead. Uploading your own image for one is not possible here yet.',
    examples: [
      ex(
        'Add parking as an add-on for $10',
        'Add an all-day parking pass as an add-on for $10, with a short description saying one pass covers one car.',
      ),
      ex(
        'Let attendees choose how many of each add-on',
        'Let attendees choose how many of each add-on they want, so a family can add two meals or three parking passes in one booking.',
      ),
      ex(
        'Remove the last add-on',
        'Remove the last add-on from this event and keep the others in the same order.',
      ),
    ],
  },

  'file-downloads': pageType => ({
    tab: CONTENT_TAB[pageType],
    path: '/file-downloads',
    summary:
      pageType === 'form'
        ? 'Add files to create a lead magnet.'
        : 'Upload files customers can download after purchase.',
    coverage: 'partial',
    coverageNote:
      'Reordering and removing files work. To add your own, share a public link or ask for an uploader.',
    examples: [
      ex(
        'Put the files in alphabetical order',
        'Put the downloadable files in alphabetical order by file name.',
      ),
      ex(
        'Move the last file to the top',
        'Move the last file to the top of the list so it is the first thing people download.',
      ),
      ex(
        'Remove the last file',
        'Remove the last file from the downloads and keep the others in the same order.',
      ),
    ],
  }),

  inventory: {
    tab: 'product',
    path: '/inventory',
    summary: 'Limit how many times this product can be purchased.',
    examples: [
      ex('Limit stock to 100', 'Limit stock to 100, so sales stop once 100 have been sold.'),
      ex(
        'Add a SKU based on the product title',
        'Add a short SKU based on the product title, in capitals with hyphens, like YOGA-MAT-BLUE.',
      ),
      ex(
        'Remove the stock limit',
        'Remove the stock limit so this product can be bought without a cap.',
      ),
    ],
  },

  'license-keys': {
    tab: 'product',
    path: '/license-keys',
    summary: 'Verify purchases in your application and manage access.',
    examples: [
      ex(
        'Turn on license keys for each purchase',
        'Turn on license keys, so every buyer gets their own unique key after they pay.',
      ),
      ex(
        'Turn on license keys and mention them in the description',
        'Turn on license keys and add a line to the description saying buyers get a license key by email after they pay.',
      ),
      ex(
        'Turn off license keys',
        'Turn off license keys, so new buyers no longer get a key after they pay.',
      ),
    ],
  },

  'ticket-types': {
    tab: 'event',
    path: '/ticket-types',
    summary: 'Set the tickets attendees can buy, with their prices and quantities.',
    examples: [
      ex(
        'Add a $20 Early bird ticket with 50 available',
        'Add an Early bird ticket type for $20 with 50 available, and keep the other tickets as they are.',
      ),
      ex(
        'End sales for the first ticket on:',
        'Stop selling the first ticket type after this date, and keep the other tickets on sale:',
      ),
      ex(
        'Hide the last ticket',
        'Hide the last ticket type so attendees no longer see it, and keep its existing bookings.',
      ),
    ],
  },

  'bulk-discounts': pageType =>
    pageType === 'event'
      ? {
          tab: 'event',
          path: '/bulk-discounts',
          summary: 'Offer discounts based on the number of tickets purchased.',
          examples: [
            ex(
              'Give 10% off when they buy 4 or more tickets',
              'Give 10% off every ticket when someone buys 4 or more in one booking, on all ticket types.',
            ),
            ex(
              'Add 20% off for groups of 10 or more tickets',
              'Add a group rate of 20% off every ticket when someone books 10 or more at once, and mention it in the description.',
            ),
            ex(
              'Remove the bulk discounts',
              'Remove all bulk discounts from this event, so every ticket is full price whatever the quantity.',
            ),
          ],
        }
      : {
          tab: 'product',
          path: '/bulk-discounts',
          summary: 'Offer discounts based on the number of items purchased.',
          examples: [
            ex(
              'Give 10% off when they buy 5 or more',
              'Give 10% off each item when someone buys 5 or more in one order.',
            ),
            ex(
              'Add tiers: 10% off for 3 or more, 20% off for 10 or more',
              'Add two discount tiers: 10% off each item for 3 or more, and 20% off each item for 10 or more.',
            ),
            ex(
              'Remove the bulk discounts',
              'Remove all bulk discounts, so every item is full price whatever the quantity.',
            ),
          ],
        },

  'checkout-fields': pageType => ({
    tab: 'fields',
    path: '/form-fields',
    summary: 'Collect customer information.',
    examples:
      pageType === 'event'
        ? [
            HEARD_ABOUT_US,
            ex(
              'Add a required emergency contact field',
              'Add a required field asking for an emergency contact name and phone number, for anyone attending in person.',
            ),
            ex(
              'Ask about any accessibility needs',
              'Add an optional field asking about any accessibility needs, such as step-free access or a seat near the front, so we can plan ahead.',
            ),
          ]
        : [
            HEARD_ABOUT_US,
            ex(
              'Add a terms and conditions checkbox they must tick',
              'Add a required checkbox asking buyers to agree to our terms and conditions before they pay.',
            ),
            ex(
              'Ask for a company name only when they tick Buying for a business',
              'Add a Buying for a business checkbox, and show a required company name field only when it is ticked.',
            ),
          ],
  }),

  fields: {
    tab: 'fields',
    path: '/form-fields',
    summary: 'Collect customer information.',
    examples: [
      ex(
        'Add a required phone number field',
        'Add a required phone number field after the email field, labeled Phone number.',
      ),
      HEARD_ABOUT_US,
      ex(
        'Ask which session they want to join',
        'Add a required dropdown asking which session they want to join, with one option for each session.',
      ),
    ],
  },

  'discount-code': {
    tab: 'fields',
    path: '/form-fields',
    sectionLabel: 'Discount code',
    summary: 'Allow customers to enter a discount code at checkout.',
    coverage: 'partial',
    coverageNote:
      'Covers the code box and codes for this page only. Make codes for more pages under Coupons.',
    examples: [
      ex(
        'Create a 20% off code called WELCOME20 for this page',
        'Create a 20% off discount code called WELCOME20 that works on this page only, and show the discount code field at checkout.',
      ),
      ex(
        'Turn the discount code field into a link',
        'Turn the discount code field into a small link that opens the field when clicked, so it takes less space at checkout.',
      ),
      ex(
        'Hide the discount code field',
        'Hide the discount code field, so people do not see a box for codes at checkout.',
      ),
    ],
  },

  'manual-payments': {
    tab: 'checkout',
    path: '/manual-payments',
    summary: 'Choose how customers pay for one-time payments at checkout.',
    examples: [
      ex(
        'Offer pay by invoice as well as paying now',
        'Let people choose between paying now at checkout and paying later by invoice.',
      ),
      ex(
        'Let people pay a 50% deposit now and the rest later',
        'Let people pay a 50% deposit now and the rest later, as well as paying the full amount up front.',
      ),
      ex(
        'Switch back to pay now only',
        'Switch back to pay now only, and turn off any invoice or deposit options.',
      ),
    ],
  },

  'payment-methods': pageType => ({
    tab: 'checkout',
    path: '/payment-methods',
    summary: 'Choose how customers can pay at checkout.',
    examples: [
      ex(
        'Show Apple Pay and Google Pay as express checkout',
        'Show Apple Pay and Google Pay as express checkout buttons at the top, so people can pay in one tap on their phone.',
      ),
      ex(
        `Let ${PEOPLE[pageType]} save their card for next time`,
        `Let ${PEOPLE[pageType]} save their card at checkout, so paying again next time is faster.`,
      ),
      ex(
        'Switch to card only',
        'Switch to card payments only and turn off the other payment methods.',
      ),
    ],
  }),

  'additional-fees': {
    tab: 'checkout',
    path: '/fees-surchages',
    summary: 'Add processing or other fees at checkout.',
    examples: [
      ex(
        'Add a 3% card processing fee',
        'Add a 3% card processing fee, shown as its own line at checkout so people see it before they pay.',
      ),
      ex(
        'Only apply the fee to card payments',
        'Only apply the additional fee to card payments, not to other payment methods.',
      ),
      ex(
        'Remove the additional fees',
        'Remove all additional fees, so people pay only the listed price plus any tax.',
      ),
    ],
  },

  tax: pageType => ({
    tab: 'checkout',
    path: '/tax',
    summary: 'Choose how tax is calculated.',
    coverage: 'partial',
    coverageNote:
      "Covers this page's tax settings, and creating a fixed tax rate to use here. New rates also appear in your account's tax rates. Setting your default tax rate is not possible here.",
    examples: [
      ex(
        'Add a 20% VAT rate to this page',
        'Create a 20% tax rate called VAT and apply it to this page.',
      ),
      ex(
        'Turn on Stripe Tax',
        'Turn on Stripe Tax, so tax is worked out automatically from where each customer is.',
      ),
      pageType === 'checkout'
        ? ex(
            'Show prices including tax',
            'Show prices including tax, so the price people see is the price they pay.',
          )
        : ex(
            'Add an optional tax ID field for business buyers',
            'Add an optional tax ID field at checkout, so business buyers can put their VAT or tax number on the invoice.',
          ),
    ],
  }),

  'purchase-limits': {
    tab: 'settings',
    path: '/limits',
    summary: 'Choose whether a customer can purchase or subscribe to this product more than once.',
    examples: [
      ex(
        'Stop people buying this twice with the same email',
        'Limit purchases to one per email address, so the same person cannot buy this twice.',
      ),
      ex(
        'Stop customers subscribing to this plan twice',
        'Stop customers starting a second subscription to this plan while they already have an active one.',
      ),
      ex(
        'Let customers buy this more than once',
        'Remove the purchase limit, so customers can buy this as many times as they like.',
      ),
    ],
  },

  'submission-limits': {
    tab: 'settings',
    path: '/limits',
    summary: 'Choose whether a lead can submit this form more than once.',
    examples: [
      ex(
        'Only allow one submission per email address',
        'Only allow one submission per email address, so each person can fill in the form once.',
      ),
      ex(
        'Only allow one submission per email and say so in the description',
        'Only allow one submission per email address, and add a line to the description saying each person can submit once.',
      ),
      ex(
        'Let people submit the form more than once',
        'Remove the submission limit, so people can submit the form as many times as they need.',
      ),
    ],
  },

  'cart-abandonment': {
    tab: 'checkout',
    path: '/abandonment',
    summary: 'Automatically send up to 3 reminder emails when a checkout is abandoned.',
    examples: [
      ex(
        'Turn off reminder emails for this page',
        'Turn off the cart abandonment reminder emails for this page, so people who leave checkout get no follow-up.',
      ),
      ex(
        'Hide the logo on reminder emails',
        'Hide the logo on the cart abandonment reminder emails and keep the rest of each email as it is.',
      ),
      ex(
        'Show the store name on reminder emails',
        'Show our store name at the top of the cart abandonment reminder emails.',
      ),
    ],
  },

  'dynamic-price': dynamicGuide('checkout', 'settings'),

  'dynamic-info': pageType => dynamicGuide(pageType, 'settings'),

  'payment-confirmation': pageType => ({
    tab: 'after-payment',
    path: '/confirmation',
    summary: 'What your customer sees after successful payment.',
    examples: [
      ex(
        'Write a custom thank-you message with next steps',
        pageType === 'event'
          ? 'Write a custom thank-you message for the confirmation page with next steps: when to arrive, what to bring and how to reach us with questions.'
          : 'Write a custom thank-you message for the confirmation page with next steps: what happens now, how they get access and how to reach us with questions.',
      ),
      pageType === 'event'
        ? ex(
            'Change the confirmation heading to See you there!',
            'Change the confirmation page heading to See you there! and keep the rest of the message as it is.',
          )
        : ex(
            "Change the confirmation heading to You're all set",
            "Change the confirmation page heading to You're all set and keep the rest of the message as it is.",
          ),
      pageType === 'event'
        ? ex(
            'Hide the booking details on the confirmation page',
            'Hide the booking details on the confirmation page and show only the thank-you message.',
          )
        : DEFAULT_CONFIRMATION,
    ],
  }),

  'after-submission': {
    tab: 'after-payment',
    path: '/confirmation',
    summary: 'What your customer sees after submitting the form.',
    examples: [
      ex(
        'Thank people and tell them to check their inbox',
        'Write a short thank-you message for after they submit, telling people to check their inbox for a confirmation email and when we will be in touch.',
      ),
      ex(
        'Change the confirmation heading to Thank you!',
        'Change the confirmation heading to Thank you! and keep the rest of the message as it is.',
      ),
      DEFAULT_CONFIRMATION,
    ],
  },

  'email-confirmation': pageType => ({
    tab: 'after-payment',
    path: '/email-confirmation',
    summary:
      pageType === 'form'
        ? 'What your customer receives after submitting the form.'
        : 'What your customer receives after successful payment.',
    examples: [
      ex(
        'Write a custom confirmation email with a warm welcome',
        {
          checkout:
            'Write a custom confirmation email with a warm welcome, what happens next and how to reach us with questions.',
          event:
            'Write a custom confirmation email with a warm welcome, the date, time and place, what to bring and how to reach us with questions.',
          form: 'Write a custom confirmation email with a warm welcome, what happens next and when we will be in touch.',
        }[pageType],
      ),
      ex(
        `Change the email subject to ${CONFIRMATION_SUBJECT[pageType]}`,
        `Change the confirmation email subject to ${CONFIRMATION_SUBJECT[pageType]} and keep the email itself as it is.`,
      ),
      ex(
        'Hide the logo on the confirmation email',
        'Hide the logo at the top of the confirmation email and keep the rest of the email as it is.',
      ),
    ],
  }),

  'one-click-upsell': {
    tab: 'after-payment',
    path: '/one-click-upsell',
    summary: 'Show an additional one-click offer after successful payment.',
    coverage: 'partial',
    coverageNote:
      'Sending buyers to your own upsell link and turning the upsell off work. Choosing an upsell checkout page is not possible here yet.',
    examples: [
      ex(
        'Redirect buyers to my own upsell page:',
        'After they pay, send buyers to my own upsell page at this link:',
      ),
      ex(
        'Change the upsell page link to:',
        'Change the link buyers are sent to for the one-click upsell after they pay to:',
      ),
      ex(
        'Turn off the upsell',
        'Turn off the one-click upsell, so buyers go straight to the confirmation page after they pay.',
      ),
    ],
  },

  invoices: {
    tab: 'settings',
    path: '/invoices',
    summary: 'Customize invoice settings for this page.',
    examples: [
      ex(
        'Make invoices due in 30 days',
        'Make invoices from this page due 30 days after they are sent.',
      ),
      ex(
        'Add our bank details to invoices:',
        'Add our bank details to invoices from this page, so people can pay by bank transfer. Our details are:',
      ),
      ex(
        'Add a thank-you note to invoices',
        'Add a short thank-you note to the bottom of invoices from this page, with how to reach us with questions.',
      ),
    ],
  },

  'email-notifications': pageType => ({
    tab: 'settings',
    path: '/email-notifications',
    summary: `Choose which emails you get about this ${pageTypeLabel(pageType)}.`,
    examples: [
      ex(
        `Turn on admin notifications for new ${NEW_RECORDS[pageType]}`,
        `Turn on admin notifications for new ${NEW_RECORDS[pageType]} on this page, so I get an email each time one comes in.`,
      ),
      ex(
        'Also send notifications to:',
        'Also send the admin notifications for this page to this address:',
      ),
      pageType === 'checkout'
        ? ex(
            'Email me when a subscription is canceled',
            'Send me an email notification whenever a subscription to this product is canceled.',
          )
        : ex('Turn off admin notifications', 'Turn off all admin notifications for this page.'),
    ],
  }),

  'tracking-pixels': {
    tab: 'settings',
    path: '/tracking-pixels',
    summary: 'Add tracking code from Google Analytics, Meta Pixel or other analytics tools.',
    examples: [
      ex(
        'Add my Google Analytics code:',
        'Add my Google Analytics tracking code to this page. My measurement ID is:',
      ),
      ex(
        'Add my Meta Pixel code:',
        'Add my Meta Pixel to this page to track visits and conversions. My pixel ID is:',
      ),
      ex(
        'Remove all tracking codes',
        'Remove all tracking codes from this page, including any analytics and pixel codes.',
      ),
    ],
  },

  'url-slug': pageType => ({
    tab: 'settings',
    path: '/url-slug',
    summary: `Customize the last part of this ${pageTypeLabel(pageType)}'s URL.`,
    examples: [
      ex(
        `Change the URL slug to match the ${TITLE[pageType]}`,
        `Change the URL slug to match the ${TITLE[pageType]}, in lowercase words joined by hyphens.`,
      ),
      ex(
        'Make the URL slug shorter',
        'Make the URL slug shorter: two or three words that are easy to say out loud and type.',
      ),
      ex('Change the URL slug to:', `Change the URL slug of this ${pageTypeLabel(pageType)} to:`),
    ],
  }),

  'google-index': pageType => ({
    tab: 'settings',
    path: '/google-index',
    summary: `Control whether this ${pageTypeLabel(pageType)} can appear in Google search results.`,
    examples: [
      ex(
        'Show this page in Google search results',
        'Let Google show this page in search results, so people can find it by searching.',
      ),
      ex(
        'Hide this page from Google search results',
        'Hide this page from Google search results, so only people with the link can find it.',
      ),
      ex(
        'Hide this page from Google, I only share the link directly',
        'Hide this page from Google search results. I only share the link directly with my members, so it should not turn up in a search.',
      ),
    ],
  }),

  'redirect-page': pageType => ({
    tab: 'settings',
    path: '/redirect-page',
    summary: `Send visitors to another page instead of this ${pageTypeLabel(pageType)}.`,
    examples: [
      ex(
        'Redirect visitors to my website:',
        `Send visitors of this ${pageTypeLabel(pageType)} to my website instead:`,
      ),
      ex(
        'Change the redirect link to:',
        `Change the link visitors of this ${pageTypeLabel(pageType)} are sent to:`,
      ),
      ex('Turn off the redirect', `Turn off the redirect for this ${pageTypeLabel(pageType)}.`),
    ],
  }),

  'pop-up-behavior': {
    tab: 'settings',
    path: '/pop-up-behavior',
    summary: 'Control how visitors close the pop-up.',
    examples: [
      ex(
        'Let visitors close the pop-up by clicking outside it',
        'Let visitors close the pop-up by clicking anywhere outside it, as well as with the close button.',
      ),
      ex(
        'Stop the pop-up closing when visitors click outside it',
        'Stop the pop-up closing when visitors click outside it, so it only closes with the close button.',
      ),
      ex(
        'Make the pop-up harder to close by accident',
        'Make the pop-up harder to close by accident, so a stray click outside it does not lose what people have typed.',
      ),
    ],
  },

  theme: pageType => ({
    tab: 'design',
    path: '/theme',
    summary: `Customize your ${pageTypeLabel(pageType)} and create reusable themes.`,
    coverage: 'partial',
    coverageNote:
      "Covers this page's design and wording. Design changes go into a theme for this page, so other pages on your theme keep their look. Deleting themes and choosing your store's default theme are not possible here.",
    examples: themeExamples,
  }),
};
